import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  LayoutDashboard,
  Ship,
  Navigation,
  Map,
  Anchor,
  Settings,
  ClipboardList,
  BarChart3,
  FileSpreadsheet,
  Clock,
  CornerDownLeft,
  ArrowUp,
  ArrowDown,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { carriers as carriersApi, terminals as terminalsApi, type Carrier, type Terminal } from '../api'
import { useAuth } from '../../core/auth'

interface CommandPaletteProps {
  open: boolean
  onClose: () => void
}

interface ResultItem {
  id: string
  label: string
  sublabel?: string
  category: 'Pages' | 'Vessels' | 'Terminals'
  path: string
  icon?: LucideIcon
}

// Atlas LNG — single product, pages in the current nav only.
const PAGE_ITEMS: ResultItem[] = [
  { id: 'p-dashboard', label: 'Dashboard', sublabel: 'LNG Performance', category: 'Pages', path: '/lng-ops', icon: LayoutDashboard },
  { id: 'p-live-view', label: 'Live View', category: 'Pages', path: '/lng-ops/live-view', icon: Map },
  { id: 'p-voyage-performance', label: 'Voyage Performance', category: 'Pages', path: '/lng-ops/voyage-performance', icon: Navigation },
  { id: 'p-vessel-reports', label: 'Vessel Reports', category: 'Pages', path: '/lng-ops/vessel-reports', icon: ClipboardList },
  { id: 'p-graphs', label: 'Graphs', category: 'Pages', path: '/lng-ops/graphs', icon: BarChart3 },
  { id: 'p-reports', label: 'Reports', category: 'Pages', path: '/lng-ops/reports', icon: FileSpreadsheet },
  { id: 'p-scheduler', label: 'Scheduler', category: 'Pages', path: '/lng-ops/scheduler', icon: Clock },
  { id: 'p-admin', label: 'Admin', sublabel: 'User management', category: 'Pages', path: '/lng-ops/admin', icon: Settings },
]

const MAX_PER_CATEGORY = 5

export default function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const inputRef = useRef<HTMLInputElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [vessels, setVessels] = useState<Carrier[]>([])
  const [terminalResults, setTerminalResults] = useState<Terminal[]>([])
  const [vesselsLoaded, setVesselsLoaded] = useState(false)
  const terminalDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Load vessels once when palette opens
  useEffect(() => {
    if (open && !vesselsLoaded) {
      carriersApi.myVessels().then((data) => {
        setVessels(data)
        setVesselsLoaded(true)
      }).catch(() => {
        setVesselsLoaded(true)
      })
    }
  }, [open, vesselsLoaded])

  // Search terminals with debounce when query changes
  useEffect(() => {
    if (!open) return
    if (terminalDebounceRef.current) clearTimeout(terminalDebounceRef.current)

    if (query.trim().length < 2) {
      setTerminalResults([])
      return
    }

    terminalDebounceRef.current = setTimeout(() => {
      terminalsApi.list({ search: query.trim() }).then((data) => {
        setTerminalResults(data.slice(0, MAX_PER_CATEGORY))
      }).catch(() => {
        setTerminalResults([])
      })
    }, 300)

    return () => {
      if (terminalDebounceRef.current) clearTimeout(terminalDebounceRef.current)
    }
  }, [query, open])

  // Focus input when opening
  useEffect(() => {
    if (open) {
      setQuery('')
      setSelectedIndex(0)
      // Small delay to ensure the modal is rendered
      requestAnimationFrame(() => {
        inputRef.current?.focus()
      })
    }
  }, [open])

  // Filter pages by query
  const filteredPages = useMemo(() => {
    const q = query.toLowerCase().trim()
    if (!q) return PAGE_ITEMS.filter((p) => p.id !== 'p-admin' || user?.role === 'admin')
    return PAGE_ITEMS
      .filter((p) => {
        if (p.id === 'p-admin' && user?.role !== 'admin') return false
        return p.label.toLowerCase().includes(q) || (p.sublabel && p.sublabel.toLowerCase().includes(q))
      })
      .slice(0, MAX_PER_CATEGORY)
  }, [query, user?.role])

  // Filter vessels by query
  const filteredVessels = useMemo(() => {
    const q = query.toLowerCase().trim()
    if (!q) return []
    return vessels
      .filter((v) => v.name.toLowerCase().includes(q) || (v.imo && v.imo.includes(q)))
      .slice(0, MAX_PER_CATEGORY)
  }, [query, vessels])

  // Build flat results list
  const results = useMemo((): ResultItem[] => {
    const items: ResultItem[] = []

    // Pages first
    for (const p of filteredPages) {
      items.push(p)
    }

    // Vessels
    for (const v of filteredVessels) {
      items.push({
        id: `v-${v.id}`,
        label: v.name,
        sublabel: v.imo ? `IMO ${v.imo}` : undefined,
        category: 'Vessels',
        path: '/lng-ops/voyage-performance',
        icon: Ship,
      })
    }

    // Terminals
    for (const t of terminalResults) {
      items.push({
        id: `t-${t.id}`,
        label: t.name,
        sublabel: [t.country, t.status].filter(Boolean).join(' \u00B7 ') || undefined,
        category: 'Terminals',
        path: '/lng-ops/live-view',
        icon: Anchor,
      })
    }

    return items
  }, [filteredPages, filteredVessels, terminalResults])

  // Clamp selection index when results change
  useEffect(() => {
    setSelectedIndex(0)
  }, [results.length])

  // Navigate to selected item
  const handleSelect = useCallback((item: ResultItem) => {
    onClose()
    navigate(item.path)
  }, [navigate, onClose])

  // Keyboard handler
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev + 1) % Math.max(results.length, 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev - 1 + results.length) % Math.max(results.length, 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (results[selectedIndex]) {
        handleSelect(results[selectedIndex])
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    }
  }, [results, selectedIndex, handleSelect, onClose])

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return
    const selected = listRef.current.querySelector('[data-selected="true"]')
    if (selected) {
      selected.scrollIntoView({ block: 'nearest' })
    }
  }, [selectedIndex])

  // Click outside to close
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      onClose()
    }
  }, [onClose])

  if (!open) return null

  // Group results by category for rendering
  const grouped: { category: string; items: ResultItem[]; startIndex: number }[] = []
  let idx = 0
  const categories = ['Pages', 'Vessels', 'Terminals'] as const
  for (const cat of categories) {
    const items = results.filter((r) => r.category === cat)
    if (items.length > 0) {
      grouped.push({ category: cat, items, startIndex: idx })
      idx += items.length
    }
  }

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/60 backdrop-blur-sm pt-[15vh]"
    >
      <div className="w-full max-w-[520px] rounded-xl bg-bg-secondary shadow-2xl overflow-hidden animate-in">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3">
          <Search className="h-4 w-4 flex-shrink-0 text-text-muted" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, vessels, terminals..."
            className="flex-1 bg-transparent text-sm text-text-primary placeholder-text-muted outline-none"
          />
          <kbd className="hidden sm:flex items-center gap-0.5 rounded-light bg-bg-primary px-1.5 py-0.5 text-2xs font-mono text-text-muted">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto p-2">
          {results.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <Search className="h-8 w-8 text-text-muted/30 mb-3" />
              <p className="text-xs text-text-muted">
                {query.trim() ? 'No results found' : 'Start typing to search...'}
              </p>
            </div>
          ) : (
            grouped.map((group) => (
              <div key={group.category} className="mb-1">
                <div className="px-2 py-1.5">
                  <span className="text-2xs font-medium text-text-muted">
                    {group.category}
                  </span>
                </div>
                {group.items.map((item, i) => {
                  const globalIdx = group.startIndex + i
                  const isSelected = globalIdx === selectedIndex
                  return (
                    <button
                      key={item.id}
                      data-selected={isSelected}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setSelectedIndex(globalIdx)}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                        isSelected
                          ? 'bg-accent/10 border-l-2 border-accent'
                          : 'border-l-2 border-transparent hover:bg-bg-hover'
                      }`}
                    >
                      {item.icon && (
                        <item.icon
                          className={`h-4 w-4 flex-shrink-0 ${isSelected ? 'text-accent' : 'text-text-muted'}`}
                          strokeWidth={1.8}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <span className={`text-xs font-medium ${isSelected ? 'text-text-primary' : 'text-text-secondary'}`}>
                          {item.label}
                        </span>
                        {item.sublabel && (
                          <span className="ml-2 text-2xs text-text-muted">
                            {item.sublabel}
                          </span>
                        )}
                      </div>
                      {isSelected && (
                        <CornerDownLeft className="h-3.5 w-3.5 flex-shrink-0 text-text-muted" />
                      )}
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hints */}
        {results.length > 0 && (
          <div className="flex items-center gap-4 px-4 py-2">
            <div className="flex items-center gap-1.5 text-2xs text-text-muted">
              <ArrowUp className="h-3 w-3" />
              <ArrowDown className="h-3 w-3" />
              <span>Navigate</span>
            </div>
            <div className="flex items-center gap-1.5 text-2xs text-text-muted">
              <CornerDownLeft className="h-3 w-3" />
              <span>Select</span>
            </div>
            <div className="flex items-center gap-1.5 text-2xs text-text-muted">
              <kbd className="rounded-light bg-bg-primary px-1 py-px text-2xs font-mono">ESC</kbd>
              <span>Close</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
